/**
 * What ⌘K can currently search.
 *
 * The console's two corpora are mounted by *sibling* routes: the Shared Docs
 * session lives under `DocsWorkspaceRoute` and the tracker room under
 * `TrackersWorkspaceRoute`, and neither is mounted while the other is showing.
 * A palette above both therefore cannot simply read two stores; it has to ask
 * whatever is live.
 *
 * So a mode registers a provider while it is mounted and the palette reads the
 * registry. Documents are the exception and do not register: the shared bundle
 * keeps its index in scope-keyed atoms that outlive the session, so a project's
 * documents stay searchable after Shared Docs has been visited once. See
 * `readDocumentEntries`.
 *
 * The honest consequence, which the palette states rather than hides: opening a
 * tracker link cold and pressing ⌘K searches tracker items only, because
 * nothing in that page load ever asked the server for a document index. Fixing
 * that would mean opening the docs index socket in every mode, which is a
 * second socket per member for a search box.
 */

import {
  getSharedDocumentDisplayName,
  getSharedDocumentDisplayPath,
  getSharedDocumentsForScopeKey,
  getSharedFoldersForScopeKey,
} from '@nimbalyst/collab-bundle/docs-ui';
import type { QuickOpenEntry } from '@nimbalyst/collab-bundle/quick-open';

/** A palette row, plus what the host does when it is chosen. */
export interface QuickOpenTarget extends QuickOpenEntry {
  /** Where selecting this row navigates. A console path, not a URL. */
  path: string;
}

export type QuickOpenProvider = () => QuickOpenTarget[];

const providers = new Map<string, QuickOpenProvider>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Register a corpus for as long as the caller is mounted. Returns the
 * unregister, so a mode can hand it straight to a `useEffect` cleanup.
 */
export function registerQuickOpenProvider(id: string, provider: QuickOpenProvider): () => void {
  providers.set(id, provider);
  notify();
  return () => {
    // Only if it is still ours: a remount can register the replacement before
    // React runs the outgoing cleanup, and deleting unconditionally would
    // unregister the live provider.
    if (providers.get(id) === provider) {
      providers.delete(id);
      notify();
    }
  };
}

/** Fires when a corpus becomes available or goes away, so a trigger can hide. */
export function onQuickOpenProvidersChanged(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function hasQuickOpenProviders(): boolean {
  return providers.size > 0;
}

/**
 * The ids currently registered.
 *
 * The palette records these when it opens so it can tell a corpus *appearing*
 * from one *going away*. A corpus that goes away while the palette is up has
 * usually been taken down for a reason the reader may not read team data any
 * more -- a revoked tracker room unmounts its registration -- and the rows it
 * contributed were copied into the palette's state, where nothing else would
 * retract them.
 */
export function quickOpenProviderIds(): string[] {
  return [...providers.keys()];
}

/**
 * The project's shared documents as palette rows.
 *
 * Read directly rather than through a provider because the bundle's index atoms
 * are keyed by scope and survive the session that filled them -- a reader who
 * has opened Shared Docs once keeps their documents searchable from Trackers.
 * `decryptFailed` rows are dropped: a row that cannot be named cannot be
 * chosen, and it would rank on an empty title.
 */
export function readDocumentEntries(
  scopeKey: string,
  documentPath: (documentId: string) => string,
): QuickOpenTarget[] {
  const folders = getSharedFoldersForScopeKey(scopeKey);
  return getSharedDocumentsForScopeKey(scopeKey)
    .filter((document) => !document.decryptFailed)
    .map((document) => {
      const displayPath = getSharedDocumentDisplayPath(document, folders);
      const title = getSharedDocumentDisplayName(displayPath, document.documentId);
      return {
        kind: 'document' as const,
        id: document.documentId,
        title,
        // The folder run, not the leaf the title already shows.
        subtitle: displayPath.includes('/')
          ? displayPath.slice(0, displayPath.lastIndexOf('/'))
          : null,
        updatedAt: document.updatedAt ?? null,
        icon: 'description',
        path: documentPath(document.documentId),
      };
    });
}

/** Everything currently registered, in registration order. */
export function readRegisteredEntries(): QuickOpenTarget[] {
  return [...providers.values()].flatMap((provider) => {
    try {
      return provider();
    } catch (error) {
      // A corpus mid-teardown must not take the palette down with it.
      console.error('[quickOpen] A registered provider threw', error);
      return [];
    }
  });
}
